import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { FileService } from '@/services/FileService';
import { FileMetadata, UploadProgress } from '@/types/models';
import { FileContextValue, FileContextState } from '@/types/ui';

const FileContext = createContext<FileContextValue | undefined>(undefined);

type FileAction =
  | { type: 'SET_FILES'; payload: FileMetadata[] }
  | { type: 'ADD_FILE'; payload: FileMetadata }
  | { type: 'REMOVE_FILE'; payload: string | number }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'UPDATE_PROGRESS'; payload: { fileId: string; progress: UploadProgress } };

const initialState: FileContextState = {
  files: [],
  uploadProgress: {},
  loading: false,
  error: null
};

function fileReducer(state: FileContextState, action: FileAction): FileContextState {
  switch (action.type) {
    case 'SET_FILES':
      return { ...state, files: action.payload };
    case 'ADD_FILE':
      return { ...state, files: [...state.files, action.payload] };
    case 'REMOVE_FILE':
      return {
        ...state,
        files: state.files.filter(file => file.id !== action.payload)
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'UPDATE_PROGRESS':
      return {
        ...state,
        uploadProgress: {
          ...state.uploadProgress,
          [action.payload.fileId]: action.payload.progress
        }
      };
    default:
      return state;
  }
}

export const FileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(fileReducer, initialState);

  const loadFiles = useCallback(async (tournamentId: number) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const files = await FileService.getFiles(tournamentId);
      dispatch({ type: 'SET_FILES', payload: files });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Ошибка при загрузке файлов' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const uploadFile = useCallback(async (tournamentId: number, file: File) => {
    const fileId = `${Date.now()}-${file.name}`;
    try {
      dispatch({
        type: 'UPDATE_PROGRESS',
        payload: { fileId, progress: { percent: 0, status: 'uploading' } }
      });

      const response = await FileService.uploadFile(
        tournamentId,
        file,
        (progress) => {
          dispatch({
            type: 'UPDATE_PROGRESS',
            payload: {
              fileId,
              progress: { percent: progress, status: 'uploading' }
            }
          });
        }
      );

      if (response.file) {
        dispatch({ type: 'ADD_FILE', payload: response.file });
      }
      dispatch({
        type: 'UPDATE_PROGRESS',
        payload: { fileId, progress: { percent: 100, status: 'done' } }
      });
    } catch (error) {
      dispatch({
        type: 'UPDATE_PROGRESS',
        payload: {
          fileId,
          progress: {
            percent: 0,
            status: 'error',
            error: 'Ошибка при загрузке файла'
          }
        }
      });
      throw error;
    }
  }, []);

  const deleteFile = useCallback(async (tournamentId: number, fileId: string) => {
    try {
      await FileService.deleteFile(tournamentId, fileId);
      dispatch({ type: 'REMOVE_FILE', payload: fileId });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Ошибка при удалении файла' });
      throw error;
    }
  }, []);

  return (
    <FileContext.Provider
      value={{
        ...state,
        loadFiles,
        uploadFile,
        deleteFile
      }}
    >
      {children}
    </FileContext.Provider>
  );
};

export const useFiles = () => {
  const context = useContext(FileContext);
  if (!context) {
    throw new Error('useFiles must be used within a FileProvider');
  }
  return context;
};